import React, { useMemo, useState } from 'react';
import { NavigateFunction } from 'react-router-dom';
import {
  TorneioPorIdData,
  CategoriaTorneio,
  InscricoesPorTorneioData,
} from '../../../services/torneioService';
import CategoriaOcupacaoBar from '../../../components/CategoriaOcupacaoBar';
import CategoriaStatusChip from '../../../components/CategoriaStatusChip';
import AbrirInscricoesEmLoteBar from './components/AbrirInscricoesEmLoteBar';
import dayjs from 'dayjs';

interface TournamentInfoTabProps {
  torneioId: string;
  torneio: TorneioPorIdData['torneioPorId'];
  categorias: CategoriaTorneio[];
  inscricoes: InscricoesPorTorneioData['inscricoesTorneioPorAtletaIdTorneioId'];
  statsCategorias: { totalCategorias: number; categoriasSimples: number; categoriasDuplas: number };
  statsInscricoes: { totalInscricoes: number; inscricoesPendentes: number; inscricoesConfirmadas: number };
  statsFilaEspera: { inscricoesFilaEspera: number; vagasDisponiveis: number };
  statsPartidas: { totalPartidas: number; partidasRealizadas: number; partidasPendentes: number };
  navigate: NavigateFunction;
  onRefresh?: () => void;
}

const TournamentInfoTab: React.FC<TournamentInfoTabProps> = ({
  torneioId,
  torneio,
  categorias,
  inscricoes: _inscricoes,
  statsCategorias,
  statsInscricoes,
  statsFilaEspera,
  statsPartidas,
  navigate,
  onRefresh,
}) => {
  const handleCreateCategory = () => {
    navigate('/admin/editcategorytournament', {
      state: { torneioId, torneioNome: torneio?.nome },
    });
  };

  const categoriasAgrupadas = useMemo(() => {
    const grupos = new Map<string, CategoriaTorneio[]>();
    const semData: CategoriaTorneio[] = [];

    categorias.forEach((cat) => {
      if (cat.dataInicio) {
        const data = dayjs(cat.dataInicio).format('YYYY-MM-DD');
        if (!grupos.has(data)) grupos.set(data, []);
        grupos.get(data)!.push(cat);
      } else {
        semData.push(cat);
      }
    });

    const ordenadas = Array.from(grupos.entries()).sort(([a], [b]) => a.localeCompare(b));
    return { ordenadas, semData };
  }, [categorias]);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const toggleSelect = (id: string) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const todasSelecionadas = categorias.length > 0 && categorias.every((c) => selectedIds.has(c.id));
  const algumasSelecionadas = categorias.some((c) => selectedIds.has(c.id));
  const toggleSelectAll = () => setSelectedIds(todasSelecionadas ? new Set() : new Set(categorias.map((c) => c.id)));

  return (
    <div className="mt-4">
      {/* Cards de Estatísticas */}
      <div className="row mb-4">
        {/* Card de Categorias */}
        <div className="col-md-3 mb-3">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body d-flex flex-column justify-content-between">
              <h5 className="card-title text-muted mb-2">Categorias</h5>
              <h2 className="fw-700 mb-1">{statsCategorias.totalCategorias}</h2>
              <p className="text-muted mb-0 small">
                {statsCategorias.categoriasSimples} simples,{' '}
                {statsCategorias.categoriasDuplas} duplas
              </p>
            </div>
          </div>
        </div>

        {/* Card de Inscrições */}
        <div className="col-md-3 mb-3">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body d-flex flex-column justify-content-between">
              <h5 className="card-title text-muted mb-2">Inscrições</h5>
              <h2 className="fw-700 mb-1">{statsInscricoes.totalInscricoes}</h2>
              <p className="text-muted mb-0 small">
                {statsInscricoes.inscricoesPendentes} pendentes,{' '}
                {statsInscricoes.inscricoesConfirmadas} confirmadas
              </p>
            </div>
          </div>
        </div>

        {/* Card de Fila de Espera */}
        <div className="col-md-3 mb-3">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body d-flex flex-column justify-content-between">
              <h5 className="card-title text-muted mb-2">Fila de Espera</h5>
              <h2 className="fw-700 mb-1">
                {statsFilaEspera.inscricoesFilaEspera}
              </h2>
              <p className="text-muted mb-0 small">
                {statsFilaEspera.vagasDisponiveis} vagas disponíveis no torneio
              </p>
            </div>
          </div>
        </div>

        {/* Card de Partidas */}
        <div className="col-md-3 mb-3">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body d-flex flex-column justify-content-between">
              <h5 className="card-title text-muted mb-2">Partidas</h5>
              <h2 className="fw-700 mb-1">{statsPartidas.totalPartidas}</h2>
              <p className="text-muted mb-0 small">
                {statsPartidas.partidasRealizadas} realizadas,{' '}
                {statsPartidas.partidasPendentes} pendentes
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Seção de Categorias */}
      <div className="card shadow-sm border-0">
        <div className="card-body p-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="fw-700 mb-0">Categorias do Torneio</h4>
            <button className="btn btn-primary" onClick={handleCreateCategory}>
              Criar Nova Categoria
            </button>
          </div>

          {categorias.length === 0 ? (
            <div className="alert alert-info" role="alert">
              Nenhuma categoria encontrada para este torneio.
            </div>
          ) : (
            <>
            <AbrirInscricoesEmLoteBar
              categorias={categorias}
              selectedIds={selectedIds}
              onClearSelection={() => setSelectedIds(new Set())}
              onRefresh={onRefresh}
            />
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={todasSelecionadas}
                        ref={(el) => { if (el) el.indeterminate = algumasSelecionadas && !todasSelecionadas; }}
                        onChange={toggleSelectAll}
                        aria-label="Selecionar todas as categorias"
                      />
                    </th>
                    <th>Nome</th>
                    <th>Tipo</th>
                    <th>Status</th>
                    <th>Vagas</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {categoriasAgrupadas.ordenadas.map(([data, cats]) => (
                    <React.Fragment key={data}>
                      <tr className="table-secondary">
                        <td colSpan={6}>
                          <strong>{dayjs(data).format('DD/MM/YYYY')}</strong>
                        </td>
                      </tr>
                      {cats.map((categoria) => (
                        <tr key={categoria.id}>
                          <td onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedIds.has(categoria.id)}
                              onChange={() => toggleSelect(categoria.id)}
                              aria-label={`Selecionar ${categoria.nome}`}
                            />
                          </td>
                          <td>
                            <strong 
                              className="text-primary" 
                              style={{ cursor: 'pointer' }}
                              onClick={() => navigate(`/liga/tournament/${torneioId}/category/${categoria.id}/manage`)}
                            >
                              {categoria.nome}
                            </strong>
                          </td>
                          <td>
                            {categoria.numeroAtletas === 1
                              ? 'Simples'
                              : categoria.numeroAtletas === 2
                              ? 'Duplas'
                              : 'Outro'}
                          </td>
                          <td>
                            <CategoriaStatusChip
                              status={categoria.status}
                              temPartidas={(categoria.fasesCategoria || []).flatMap((f) => f.partidas || []).filter((p) => p.status !== 'CANCELADA').length > 0}
                            />
                          </td>
                          <td><CategoriaOcupacaoBar ocupacao={categoria.ocupacao} /></td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() =>
                                navigate(`/liga/tournament/${torneioId}/category/${categoria.id}`, {
                                  state: {
                                    torneioId,
                                    torneioNome: torneio?.nome,
                                  },
                                })
                              }
                            >
                              Editar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  {categoriasAgrupadas.semData.length > 0 && (
                    <React.Fragment>
                      <tr className="table-secondary">
                        <td colSpan={6}>
                          <strong>Sem Data Definida</strong>
                        </td>
                      </tr>
                      {categoriasAgrupadas.semData.map((categoria) => (
                        <tr key={categoria.id}>
                          <td onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedIds.has(categoria.id)}
                              onChange={() => toggleSelect(categoria.id)}
                              aria-label={`Selecionar ${categoria.nome}`}
                            />
                          </td>
                          <td>
                            <strong 
                              className="text-primary" 
                              style={{ cursor: 'pointer' }}
                              onClick={() => navigate(`/liga/tournament/${torneioId}/category/${categoria.id}/manage`)}
                            >
                              {categoria.nome}
                            </strong>
                          </td>
                          <td>
                            {categoria.numeroAtletas === 1
                              ? 'Simples'
                              : categoria.numeroAtletas === 2
                              ? 'Duplas'
                              : 'Outro'}
                          </td>
                          <td>
                            <CategoriaStatusChip
                              status={categoria.status}
                              temPartidas={(categoria.fasesCategoria || []).flatMap((f) => f.partidas || []).filter((p) => p.status !== 'CANCELADA').length > 0}
                            />
                          </td>
                          <td><CategoriaOcupacaoBar ocupacao={categoria.ocupacao} /></td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() =>
                                navigate(`/liga/tournament/${torneioId}/category/${categoria.id}`, {
                                  state: {
                                    torneioId,
                                    torneioNome: torneio?.nome,
                                  },
                                })
                              }
                            >
                              Editar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  )}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TournamentInfoTab;

